import { OPEN_CONFIRM_CLEAR_ARRAYS_MODAL } from 'actions';
import { showErrorAlert } from '__common/modules/alerts';
import { dispatch, state } from '__common/store';
import { REMOVE_PANELS } from '../panelsActions';
import { constructGraph } from './dividePanelsintoSubArrays';

export interface SingleColumnSpacingPx {
  rowSpacingPx: number;
  columnSpacingPx: number;
}

const lastShownPendingRemovalCountByRoof = new Map<number, number>();

const getSingleColumnSpacingInPixels = (spacingPx?: SingleColumnSpacingPx) => {
  if (spacingPx) {
    return spacingPx;
  }

  const { settings: { rowSpacing, columnSpacing }, background: { metersPerPixel } } = state();

  return {
    rowSpacingPx: rowSpacing / metersPerPixel,
    columnSpacingPx: columnSpacing / metersPerPixel,
  };
};

const getPendingRemovalMessage = (count: number) => {
  const modules = count === 1 ? 'module is' : 'modules are';
  const pronoun = count === 1 ? 'it' : 'they';

  return `${count} standalone ${modules} marked in red. Connect ${pronoun} to another module in the row or ${pronoun} will be removed when you leave this roof area.`;
};

export function getSingleColumnPendingRemovalIds(panels: panelInState[], spacingPx?: SingleColumnSpacingPx): number[] {
  const { rowSpacingPx, columnSpacingPx } = getSingleColumnSpacingInPixels(spacingPx);
  const graph = constructGraph(panels, rowSpacingPx, columnSpacingPx);

  return graph.reduce<number[]>((pendingRemovalIds, node) => {
    const standaloneInRow = !node.left && !node.right;

    if (!standaloneInRow) {
      return pendingRemovalIds;
    }

    const topHasEW = node.top && (node.top.left || node.top.right);
    const bottomHasEW = node.bottom && (node.bottom.left || node.bottom.right);

    if (!topHasEW && !bottomHasEW) {
      pendingRemovalIds.push(node.id);
    }

    return pendingRemovalIds;
  }, []);
}

export function applySingleColumnPendingRemovalState(panels: panelInState[], spacingPx?: SingleColumnSpacingPx): panelInState[] {
  const pendingRemovalIds = new Set(getSingleColumnPendingRemovalIds(panels, spacingPx));

  return panels.map(panel => {
    const pendingRemoval = pendingRemovalIds.has(panel.id);

    if (panel.pendingRemoval === pendingRemoval) {
      return panel;
    }

    return {
      ...panel,
      pendingRemoval,
    };
  });
}

export function getSingleColumnPendingRemovalPanels(panels: panelInState[] = state().panels.panels): panelInState[] {
  return panels.filter(panel => panel.pendingRemoval);
}

export function showSingleColumnPendingRemovalAlert(count: number) {
  const { background: { selectedRoofId } } = state();
  const roofId = selectedRoofId || -1;

  if (!count) {
    lastShownPendingRemovalCountByRoof.delete(roofId);
    return;
  }

  if (lastShownPendingRemovalCountByRoof.get(roofId) === count) {
    return;
  }

  lastShownPendingRemovalCountByRoof.set(roofId, count);
  const isLite = !!(state() as any)?.app?.isUbuilderLite;
  if (!isLite) { showErrorAlert(getPendingRemovalMessage(count)); }
}

export function confirmSingleColumnPendingRemoval(onContinue: Function): boolean {
  const pendingPanels = getSingleColumnPendingRemovalPanels();

  if (!pendingPanels.length) {
    onContinue();
    return false;
  }

  const count = pendingPanels.length;
  const moduleLabel = count === 1 ? 'module' : 'modules';
  const verb = count === 1 ? 'is' : 'are';
  const pronoun = count === 1 ? 'it' : 'them';

  dispatch(OPEN_CONFIRM_CLEAR_ARRAYS_MODAL(
    `Remove ${count} red ${moduleLabel} before leaving?`,
    `These red ${moduleLabel} ${verb} standalone and must be connected to at least one other module in a row. Remove ${pronoun} and continue, or stay here to fix the layout.`,
    () => {
      dispatch(REMOVE_PANELS(pendingPanels.map(panel => panel.id)));
      onContinue();
    },
    null,
    null,
    null,
    'REMOVE RED MODULES AND CONTINUE',
    'CLOSE AND MODIFY PANELS',
    { dialogStyle: { opacity: 0.85 } },
    true,
  ));

  return true;
}
